import { z } from "zod";

const identifierSchema = z.string().trim().min(1).max(200);
const nonEmptyTextSchema = z.string().trim().min(1);

export const directoryJobStatusSchema = z.enum([
  "QUEUED",
  "RUNNING",
  "SUCCEEDED",
  "CANCELLED",
  "FAILED",
]);

const directoryDecisionSchema = z
  .object({
    relativePath: nonEmptyTextSchema,
    decision: z.enum(["included", "excluded", "blocked"]),
    reason: nonEmptyTextSchema.optional(),
  })
  .strict();

const rankedDirectorySourceSchema = z
  .object({
    relativePath: nonEmptyTextSchema,
    score: z.number().int().nonnegative(),
  })
  .strict();

export const directoryJobObservationSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    jobId: identifierSchema,
    projectId: identifierSchema,
    rootLabel: nonEmptyTextSchema,
    status: directoryJobStatusSchema,
    attempt: z.number().int().nonnegative(),
    summary: nonEmptyTextSchema,
    errorCode: nonEmptyTextSchema.optional(),
    preview: z
      .object({
        counts: z
          .object({
            included: z.number().int().nonnegative(),
            excluded: z.number().int().nonnegative(),
            blocked: z.number().int().nonnegative(),
          })
          .strict(),
        totalIncludedBytes: z.number().int().nonnegative(),
        decisions: z.array(directoryDecisionSchema),
        rankedSources: z.array(rankedDirectorySourceSchema),
      })
      .strict()
      .optional(),
  })
  .strict();

export const directoryCommandSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("select_directory"),
      input: z
        .object({
          projectId: identifierSchema,
          revisionId: identifierSchema,
          query: nonEmptyTextSchema.max(1000),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      type: z.enum(["inspect_directory_job", "cancel_directory_job"]),
      jobId: identifierSchema,
    })
    .strict(),
]);

export type DirectoryJobStatus = z.infer<typeof directoryJobStatusSchema>;
export type DirectoryJobObservation = z.infer<
  typeof directoryJobObservationSchema
>;
export type DirectoryCommand = z.infer<typeof directoryCommandSchema>;

export interface DirectoryCommandPort {
  execute(command: DirectoryCommand): Promise<DirectoryJobObservation>;
}
